import { Component, Inject, ElementRef, HostListener, OnInit } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import { SwUpdate } from '@angular/service-worker';
import Rollbar from 'rollbar';
import { HttpClient } from '@angular/common/http';

import { RollbarService } from '../rollbar_config';
import { SecurityService } from './core/security/security.service';
import { UserJsVm } from './shared/generated/Administration/Models/UserJsVm';
import { pcgSettings } from './shared/generated/pcg-settings';
import { AppDatabase } from '../local-db/services/init.ldb.service';
import { SetLocaldbTablesService } from '../app/services/service-workers/set-localdb-tables.service'
import { GlobalService } from './shared/service/global.service';
import { CheckNetworkService } from '../local-db/services/check-network.service';
import { CheckForUpdateService } from './services/service-workers/check-for-update.service';
import { filter, map } from 'rxjs/operators';

/**
 * This is the application's root component.
 */
@Component({
    selector: 'pcg-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit {

    errorCache: any = {};
	user: UserJsVm;
	updateCheckText = '';

	precacheCached = false;
	isOnline: boolean;

	ignoredErrorCodes: number[] = [
		0, 		// Network Error / Aborted Request
		401, 	// Login redirect
		403, 	// Access denied
		467,	// 2fa not enabled
		499,	// First time Inventory Setup custom redirect
		503, 	// Service Unavailable
		504 	// Gateway timeout
	];

	isMobile: boolean;
	@HostListener('window:resize') onResize() { this.isMobile = GlobalService.setIsMobile(window.innerWidth); }

	@HostListener("window:online", ["$event"]) onlineEvent(event) { 
		this.uploadBarcodesFromLocalDb(event);
		this.uploadUsersFromLocalDb(event); 
	}

	constructor(
		private sec: SecurityService
		, private router: Router
		, private elRef: ElementRef
		, private modalConfig: NgbModalConfig
		, private swUpdate: SwUpdate
		, private http: HttpClient
		, private localDb: AppDatabase
		, private localDbService: SetLocaldbTablesService
		, private sw: CheckForUpdateService
        , @Inject(RollbarService) private rollbar: Rollbar
	) {
		this.updateCheck();
		this.configureModal();
		this.configureRollbar(); // Set global rollbar settings

		sec.user$.subscribe(user => {
			const isLoggedIn = user ? true : false;
			if (isLoggedIn) { elRef.nativeElement.classList.add('is-logged-in'); }
			else { elRef.nativeElement.classList.remove('is-logged-in'); }
		});
		window['$router'] = router;
	}

	ngOnInit() {
		this.isOnline = CheckNetworkService.checkNetworkStatus();
		this.isMobile = GlobalService.setIsMobile(window.innerWidth);

        this.router.events.pipe(
            filter((e) =>
                e instanceof NavigationStart 
                || e instanceof NavigationEnd 
                || e instanceof NavigationCancel 
                || e instanceof NavigationError
            )
            , map((e) => e instanceof NavigationStart)
        ).subscribe(e => { 
            if (!e) {
                if ("serviceWorker" in navigator) {
                    navigator.serviceWorker.register(
                        'ngsw-worker.js'
                        , { updateViaCache : 'none' }
                    ).then((reg) => { reg.update(); }) // update the service worker on every page change
                }
            }
        });

		if (!this.isOnline) {
			// get data from localDb if offline and there is something in the localDb
			this.localDb?.table("EditUserTbl")?.toArray()
				.then((response) => { 
					if (response?.length > 0) {
						let filteredResponse = response
							.filter(o => 
								o?.userId === this.user?.id 
								&& this.user?.id !== 0
							);
						if (filteredResponse?.length > 0) { 
							this.user.firstName = filteredResponse[filteredResponse.length - 1]?.firstName;
							this.user.lastName = filteredResponse[filteredResponse.length - 1]?.lastName;
						}
					}
				}
			);
		}
	}

	updateCheck(): void {
		this.sw.checkForUpdates();
	}

	uploadUsersFromLocalDb(event) { // ran when user comes back online
		if (event.type == "online") {
			this.localDb.table("EditUserTbl").toArray()
				.then((response) => { 
					let filteredResponse = response.filter(o => o.isUpdatedOffline);
					if (filteredResponse.length > 0) { // means there are entries that have been updated
						for (let x = 0; x < filteredResponse.length; x++) {
							this.http.post("api/Administration/Users/SaveUser", filteredResponse[x])
								.subscribe((response: any) => { 
									this.localDbService.setLocalDbTable("EditUserTbl", response.model, false);
								}
							)
						}
					}
				}
			);
		}
	}

	uploadDispenseEventFromLocalDb() {
		this.localDb.table("DispensingEventTbl").toArray()
			.then((response) => { 
				let filteredResponse = response.filter(o => o.isUpdatedOffline);
				if (filteredResponse.length > 0) { // means there are entries that have been updated
					for (let x = 0; x < filteredResponse.length; x++) {
						this.http.post("api/Events/DispensingEventDetail/UpsertDispensingEvent", filteredResponse[x])
							.subscribe((response: any) => { 
								this.localDbService.setLocalDbTable("DispensingEventTbl", response.model, false);
							}
						)
					}
				}
			}
		);
	}

	uploadBarcodesFromLocalDb(event) {
		if (event.type == "online") {
			this.localDb.table("BarcodeTbl").toArray()
				.then((response) => { 
					let filteredResponse = response.filter(o => o.isUpdatedOffline);
					if (filteredResponse.length > 0) { // means there are entries that have been updated
						let uploadBarcodeCount = 0;
						for (let x = 0; x < filteredResponse.length; x++) {
							this.http.post("api/Administration/Barcode/CreateOrUpdateBarcode", filteredResponse[x])
								.subscribe((response: any) => { 
									uploadBarcodeCount++;
									this.localDbService.setLocalDbTable("BarcodeTbl", response.model, false);
									if (uploadBarcodeCount == filteredResponse.length) {
										this.uploadDispenseEventFromLocalDb();
									}
								}
							)
						}
					} else { this.uploadDispenseEventFromLocalDb(); }
				}
			);
		}
	}

	/** Set default ng-bootstrap modal options */
	configureModal() {
		// Disable ng bootstrap modal animations by default
		this.modalConfig.animation = false;
		this.modalConfig.ariaLabelledBy = 'modal-basic-title';
	}

	/** Configure rollbar throughout the website */
	configureRollbar() {
		this.rollbar.configure({
			// Set initial user on load (updated in subscription below)
			payload: {sessionId: this.getRollbarPayload(this.sec.getUser())},
			checkIgnore: (uncaught, args, item) => {
				// Prevent duplicate message/URL combinations for the session
				// Don't want to flood rollbar with errors if Angular starts looping
				// an error message.
				const theItem = item as any;
				const errorKey = (theItem?.body?.message?.body ?? '') 
					+ (theItem?.body?.message?.extra?.url?.split('?')[0] ?? '') 
					+ window.location.href;
				if (this.errorCache[errorKey]) { return true; }
				// Do not report these errors as they are results of intented system behavior.
				if (this.ignoredErrorCodes.includes(theItem?.body?.message?.extra?.status)) { return true; }
				this.errorCache[errorKey] = true;
				// Prevent errors in local development
				if (pcgSettings.mode === 'Development') { return true; }

				return false;
			},
		});

		// Update user if it changes
		this.sec.user$.subscribe(newUser => { this.rollbar.configure({ payload: {sessionId: this.getRollbarPayload(newUser)} }); });
	}

	/**
	 * Creates a rollbar payload with user added
	 * @param user The user to add
	 */
	getRollbarPayload(user: UserJsVm) {
		return {
			environment: pcgSettings.mode
			, client: {
				javascript: {
					source_map_enabled: true
					, code_version: '19f75265'
					, guess_uncaught_frames: true
				}
			}
			, person: !user
				? undefined
				: {
					user_id: user.id.toString()
					, firstname: user.firstName
					, lastname: user.lastName
				}
		};
	}	
}
